/**
 * OmegaShell — the OMEGA-only layout shell.
 *
 * Per spec §4.1, this shell is *distinct* from the regular
 * `RegularUserShell` and is reachable only via the `/omega` route
 * tree. It does not appear elsewhere in the UI; nothing in the
 * regular shell links into it.
 *
 * Phase-1 layout: top bar + main column. The left rail (per spec)
 * is deferred to Phase-N — at L1 the brain navigator is the
 * 6-tile grid itself, so duplicating it as a rail is noise. The
 * rail will land alongside L2 when there are organs to navigate.
 *
 * Children render inside an `<Outlet />` so nested routes
 * (`/omega/brain/:id` etc.) compose into the same shell without
 * reflowing the top bar on every navigation.
 */

import type { ReactNode } from 'react';
import { Outlet } from 'react-router-dom';

import { OmegaTopBar } from './OmegaTopBar';
import '../theme/omega-theme.css';

/**
 * The shell accepts either explicit `children` (Phase-1 leaf-route
 * usage — `<OmegaShell><OmegaTopPage /></OmegaShell>`) or, when used
 * as a layout route, falls through to the router `<Outlet />` so
 * `/omega/brain/:brainId` etc. compose into the same chrome.
 */
export function OmegaShell({ children }: { children?: ReactNode }) {
  return (
    <div className="omega-shell">
      <OmegaTopBar />
      <main className="omega-main">{children ?? <Outlet />}</main>
    </div>
  );
}
